import Vue from 'vue'
import { saveBlob } from '@/utils/excel'

export const ASSET_TYPE = {
  VIDEO: 'VIDEO',
  LOGO: 'LOGO'
}

export const UPLOAD_STATUS = {
  UPLOADING: 1,
  SUCCESS: 2,
  FAILED: 3
}

export const GOOGLE_ADS_DIRECTORY = 'launch/google-ads'

function http() {
  return Vue.prototype.$http
}

function unwrap(response) {
  const data = response && response.data
  if (data && data.header && data.header.code !== 0) {
    throw new Error(data.header.msg || '请求失败')
  }
  return data
}

function toBizError(error) {
  const data = error && error.response && error.response.data
  if (data && data.header && data.header.msg) {
    return new Error(data.header.msg)
  }
  if (data && typeof data === 'object' && typeof data.msg === 'string') {
    return new Error(data.msg)
  }
  return error instanceof Error ? error : new Error((error && error.message) || '请求失败')
}

async function request(fn) {
  try {
    return unwrap(await fn())
  } catch (error) {
    throw toBizError(error)
  }
}

function authConfig(extra = {}) {
  const token = localStorage.getItem('token') || ''
  const headers = extra.headers ? { ...extra.headers } : {}
  if (token) headers.Authorization = `Bearer ${token}`
  return {
    withCredentials: true,
    ...extra,
    headers
  }
}

export function formatCustomerId(id) {
  const digits = String(id || '').replace(/\D/g, '')
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return id ? String(id) : ''
}

export function customerOptionLabel(item) {
  if (!item) return ''
  const idLabel = item.label || formatCustomerId(item.customerId)
  return item.webSiteUrl ? `${idLabel} · ${item.webSiteUrl}` : idLabel
}

export function normalizeCustomerList(data) {
  return (data || [])
    .map((item) => {
      if (item == null) return null
      if (typeof item === 'string' || typeof item === 'number') {
        const customerId = String(item).trim()
        return customerId
          ? { customerId, webSiteUrl: '', label: formatCustomerId(customerId) }
          : null
      }
      const customerId = String(item.customerId || item.id || '').trim()
      if (!customerId) return null
      const webSiteUrl = String(item.webSiteUrl || item.websiteUrl || item.site || '').trim()
      return {
        customerId,
        webSiteUrl,
        name: item.name || '',
        label: formatCustomerId(customerId)
      }
    })
    .filter(Boolean)
}

export function mapAssetTypeLabel(type) {
  return type === ASSET_TYPE.LOGO ? '徽标' : '广告视频'
}

export function mapUploadStatus(status) {
  const value = Number(status)
  if (value === UPLOAD_STATUS.SUCCESS) return { label: '上传成功', type: 'success' }
  if (value === UPLOAD_STATUS.UPLOADING) return { label: '上传中', type: 'warning' }
  if (value === UPLOAD_STATUS.FAILED) return { label: '上传失败', type: 'danger' }
  return { label: '未知状态', type: 'info' }
}

function pickList(data) {
  if (Array.isArray(data)) return data
  if (!data || typeof data !== 'object') return []
  const nested = data.list || data.records || data.content || data.rows || data.data
  return Array.isArray(nested) ? nested : []
}

function pickTotal(body, list) {
  if (!body || typeof body !== 'object') return list.length
  if (typeof body.total === 'number') return body.total
  const data = body.data
  if (data && typeof data === 'object') {
    if (typeof data.total === 'number') return data.total
    if (typeof data.totalElements === 'number') return data.totalElements
  }
  return list.length
}

function normalizePagedList(response) {
  const body = (response && response.body) || {}
  const list = pickList(body.data)
  return {
    ...response,
    body: {
      ...body,
      data: list,
      total: pickTotal(body, list)
    }
  }
}

export function formatBudgetMicros(micros) {
  if (micros == null || micros === '') return '-'
  const value = Number(micros)
  if (Number.isNaN(value)) return String(micros)
  const amount = value / 1000000
  const text = Number.isInteger(amount) ? String(amount) : amount.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
  return `${text}/日`
}

function filenameFromDisposition(disposition, fallback) {
  if (!disposition) return fallback
  const utfMatch = disposition.match(/filename\*=UTF-8''([^;]+)/i)
  if (utfMatch) {
    try {
      return decodeURIComponent(utfMatch[1].replace(/"/g, ''))
    } catch (error) {
      return utfMatch[1]
    }
  }
  const asciiMatch = disposition.match(/filename="?([^"]+)"?/i)
  return asciiMatch ? asciiMatch[1] : fallback
}

function downloadByUrl(url, filename) {
  const link = document.createElement('a')
  link.href = url
  if (filename) link.download = filename
  link.target = '_blank'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

async function parseBlobPayload(blob) {
  const text = await blob.text()
  try {
    return JSON.parse(text)
  } catch (error) {
    return null
  }
}

async function downloadExport(url, params, fallbackName) {
  try {
    const response = await http().get(url, authConfig({
      params,
      responseType: 'blob',
      timeout: 60000
    }))
    const blob = response.data
    const contentType = String(response.headers['content-type'] || '')

    if (contentType.includes('application/json') || blob.type === 'application/json') {
      const json = await parseBlobPayload(blob)
      if (!json) throw new Error('导出失败')
      if (json.header && json.header.code !== 0) {
        throw new Error(json.header.msg || '导出失败')
      }
      const fileUrl = json.body && json.body.data
      if (typeof fileUrl === 'string' && fileUrl) {
        downloadByUrl(fileUrl, fallbackName)
        return { mode: 'url' }
      }
      return { mode: 'empty' }
    }

    const filename = filenameFromDisposition(response.headers['content-disposition'], fallbackName)
    saveBlob(blob, filename)
    return { mode: 'blob' }
  } catch (error) {
    throw toBizError(error)
  }
}

export async function listCustomers() {
  const data = await request(() =>
    http().get('/launch/google/ads/getCustomerIdList', authConfig())
  )
  return {
    ...data,
    body: {
      ...(data.body || {}),
      data: normalizeCustomerList(data.body && data.body.data)
    }
  }
}

export async function listAssets(params = {}) {
  const query = {
    pageNum: params.pageNum,
    pageSize: params.pageSize
  }
  if (params.customerId) query.customerId = params.customerId
  const data = await request(() =>
    http().get('/launch/google/ads/asset/list', authConfig({ params: query }))
  )
  return normalizePagedList(data)
}

export async function uploadFiles(files, directory = GOOGLE_ADS_DIRECTORY) {
  const form = new FormData()
  files.forEach((file) => form.append('files', file))
  form.append('directory', directory)
  const data = await request(() =>
    http().post('/launch/file/upload', form, authConfig({
      timeout: 10 * 60 * 1000
    }))
  )
  const urls = (data.body && data.body.data) || []
  if (!urls.length) {
    throw new Error('文件上传成功，但未返回素材地址')
  }
  return urls
}

export async function submitAssets({ customerId, assetType, urls }) {
  return request(() =>
    http().post('/launch/google/ads/asset/submit', {
      customerId,
      assetType,
      urls
    }, authConfig({
      timeout: 60000
    }))
  )
}

export async function uploadAndSubmitAssets({ customerId, assetType, files }) {
  const urls = await uploadFiles(files)
  await submitAssets({ customerId, assetType, urls })
  return urls
}

export async function exportAssets(params = {}) {
  const query = {}
  if (params.customerId) query.customerId = params.customerId
  return downloadExport('/launch/google/ads/asset/export', query, 'Google素材列表.xlsx')
}

export async function downloadAdTemplate() {
  const data = await request(() =>
    http().get('/launch/google/ads/ads/template/download', authConfig())
  )
  const url = data.body && data.body.data
  if (!url) throw new Error('未获取到模板地址')
  downloadByUrl(url, 'Google广告创建模板.xlsx')
  return url
}

export async function exportAds(params = {}) {
  const query = {}
  if (params.customerId) query.customerId = params.customerId
  return downloadExport('/launch/google/ads/export', query, 'Google广告列表.xlsx')
}

export async function importAds(file) {
  const form = new FormData()
  form.append('file', file)
  return request(() =>
    http().post('/launch/google/ads/import', form, authConfig({
      timeout: 10 * 60 * 1000
    }))
  )
}

export async function listAds(params = {}) {
  const query = {
    pageNum: params.pageNum,
    pageSize: params.pageSize
  }
  if (params.customerId) query.customerId = params.customerId
  const data = await request(() =>
    http().get('/launch/google/ads/list', authConfig({ params: query }))
  )
  return normalizePagedList(data)
}
